import { lazy, Suspense, useEffect, useRef, useState } from 'react';

import { useAuth } from '@shared/auth/use-auth';
import { useSupportDialogStore } from '@shared/stores/support-dialog.store';

import { SupportServiceProvider } from '../services/provider';
import { useSupportDraftStore } from '../stores/support-draft.store';

const SupportDialog = lazy(() =>
  import('./SupportDialog').then((m) => ({ default: m.SupportDialog })),
);

/**
 * App-root host of the Support dialog (the sidebar menu opens it through the
 * shared `support-dialog` store). The dialog code and its translations are
 * loaded on first open and the dialog then stays mounted, so closing it while a
 * request is in flight does not tear anything down.
 */
export function SupportDialogHost() {
  const open = useSupportDialogStore((s) => s.open);
  const closeSupport = useSupportDialogStore((s) => s.closeSupport);
  const [everOpened, setEverOpened] = useState(false);
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const previousUserId = useRef(userId);

  useEffect(() => {
    if (open) setEverOpened(true);
  }, [open]);

  // The draft belongs to the signed-in user: drop it on log out or when the user changes.
  useEffect(() => {
    if (previousUserId.current !== userId) {
      previousUserId.current = userId;
      useSupportDraftStore.getState().reset();
      closeSupport();
    }
  }, [userId, closeSupport]);

  if (!open && !everOpened) return null;

  return (
    <SupportServiceProvider>
      <Suspense fallback={null}>
        <SupportDialog />
      </Suspense>
    </SupportServiceProvider>
  );
}
